import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router";

import { Column } from "@/components/Column";
import { DashboardTableContainer } from "@/components/dashboard/DashboardTableContainer";
import { CalendarAndTasks } from "@/components/dashboard/seven-day-schedule/CalendarAndTasks";
import { DashboardScheduleSkeleton } from "@/components/dashboard/seven-day-schedule/DashboardScheduleSkeleton";
import { DashboardScheduleWeekExpandFab } from "@/components/dashboard/seven-day-schedule/DashboardScheduleWeekExpandFab";

import {
  DASHBOARD_CALENDAR_EXPANDED_KEY,
  DASHBOARD_GRID_FAB_COLUMN_CLASS,
  DASHBOARD_GRID_FULL_WIDTH_COLUMN_CLASS,
  DASHBOARD_GRID_SCHEDULE_COLUMN_CLASS,
  DASHBOARD_GRID_TABLE_COLUMN_CLASS,
} from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  DashboardJob,
  DashboardSchedulePanelView,
  DashboardSelectedUser,
  DashboardSortState,
} from "@/types";

// Persists the calendar's mini/expanded choice across reloads.
const useCalendarExpanded = (): [boolean, (next: boolean) => void] => {
  const [expanded, setExpandedState] = useState<boolean>(() => {
    try {
      return localStorage.getItem(DASHBOARD_CALENDAR_EXPANDED_KEY) === "true";
    } catch {
      return false;
    }
  });
  const setExpanded = useCallback((next: boolean) => {
    setExpandedState(next);
    try {
      localStorage.setItem(
        DASHBOARD_CALENDAR_EXPANDED_KEY,
        next ? "true" : "false",
      );
    } catch {
      // ignore — localStorage unavailable
    }
  }, []);
  return [expanded, setExpanded];
};

export const DashboardMainContent = ({
  isGroupedByStaffer,
  onGroupedByStafferChange,
  selectedUserId,
  sort,
  sortedJobs,
  onSortChange,
  onReviewJob,
  onReviewSearchJob,
  isReady,
}: {
  isGroupedByStaffer: boolean;
  onGroupedByStafferChange: (next: boolean) => void;
  selectedUserId: DashboardSelectedUser;
  sort: DashboardSortState | null;
  sortedJobs: DashboardJob[];
  onSortChange: (next: DashboardSortState | null) => void;
  onReviewJob: (job: DashboardJob, groupStafferId?: number) => void;
  onReviewSearchJob: (job: DashboardJob) => void;
  // The two heaviest surfaces reveal together once everything above is ready.
  isReady: boolean;
}) => {
  const [calendarExpanded, setCalendarExpanded] = useCalendarExpanded();
  const [panelView, setPanelView] = useState<DashboardSchedulePanelView>(
    DashboardSchedulePanelView.CALENDAR,
  );

  // Notifications deep-link to ?panel=tasks — open the schedule expanded.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("panel") === "tasks") {
      setCalendarExpanded(true);
      setPanelView(DashboardSchedulePanelView.TASKS);
    }
  }, [searchParams, setCalendarExpanded]);

  // Expanded: the schedule takes over, so this row spans the full grid width
  // (its own internal flex layout, unrelated to the stat-row columns).
  const renderExpanded = () => (
    <div
      className={cn(
        "flex min-h-0 min-w-0 flex-1 items-stretch gap-1",
        DASHBOARD_GRID_FULL_WIDTH_COLUMN_CLASS,
      )}
    >
      <DashboardTableContainer
        calendarExpanded
        isGroupedByStaffer={isGroupedByStaffer}
        onGroupedByStafferChange={onGroupedByStafferChange}
        sort={sort}
        sortedJobs={sortedJobs}
        onSortChange={onSortChange}
        onReviewJob={onReviewJob}
        onReviewSearchJob={onReviewSearchJob}
        isLoading={!isReady}
        selectedUserId={selectedUserId}
      />
      <Column className="h-full w-1 shrink-0">
        <DashboardScheduleWeekExpandFab
          isExpanded
          onToggle={setCalendarExpanded}
        />
      </Column>
      <Column className="h-full min-h-0 min-w-0 flex-1">
        {isReady ? (
          <CalendarAndTasks
            calendarExpanded
            selectedUserId={selectedUserId}
            panelView={panelView}
            onPanelViewChange={setPanelView}
          />
        ) : (
          <DashboardScheduleSkeleton variant="expanded" />
        )}
      </Column>
    </div>
  );

  // Minimized: table / fab / schedule land in the same page-level grid columns
  // (1/4, 4, 5) as the stat row, so the schedule panel and the Top Candidates
  // card above it share column 5 — identical width by construction.
  const renderMinimized = () => (
    <>
      <Column
        className={cn(
          "h-full min-h-0 min-w-0 overflow-hidden",
          DASHBOARD_GRID_TABLE_COLUMN_CLASS,
        )}
      >
        <DashboardTableContainer
          calendarExpanded={false}
          isGroupedByStaffer={isGroupedByStaffer}
          onGroupedByStafferChange={onGroupedByStafferChange}
          sort={sort}
          sortedJobs={sortedJobs}
          onSortChange={onSortChange}
          onReviewJob={onReviewJob}
          onReviewSearchJob={onReviewSearchJob}
          isLoading={!isReady}
          selectedUserId={selectedUserId}
        />
      </Column>
      <Column className={cn("h-full min-h-0", DASHBOARD_GRID_FAB_COLUMN_CLASS)}>
        <DashboardScheduleWeekExpandFab
          isExpanded={false}
          onToggle={setCalendarExpanded}
        />
      </Column>
      <Column
        className={cn(
          "h-full min-h-0 min-w-0 overflow-hidden",
          DASHBOARD_GRID_SCHEDULE_COLUMN_CLASS,
        )}
      >
        {isReady ? (
          <CalendarAndTasks
            calendarExpanded={false}
            selectedUserId={selectedUserId}
            panelView={panelView}
            onPanelViewChange={setPanelView}
          />
        ) : (
          <DashboardScheduleSkeleton variant="mini" />
        )}
      </Column>
    </>
  );

  const render = () =>
    calendarExpanded ? renderExpanded() : renderMinimized();

  return render();
};
